import { Application, Container, Filter, Loader, Renderer, Ticker } from 'pixi.js';

import Stats from 'stats.js';

import { Tween } from '../tween/Tween';
import { CanvasManager } from './CanvasManager';
import { LayerManager } from './LayerManager';

type InitializationParams = {
  width: number;
  height: number;
  antialias?: boolean;
  resolution?: number;
  transparent?: boolean;
  debug?: boolean;
};

export class Game {
  public static width: number;
  public static height: number;
  public static debug: boolean = false;
  public static renderer: Renderer;
  public static loader: Loader;
  public static ticker: Ticker;
  public static layerManager: LayerManager;
  private static stage: Container | null = null;
  private static updateErrorHandler: (e: Error) => void;
  private static readonly overlayFilters: Filter[] = [];

  private constructor() {
    throw new Error('Constructor is not allowed.');
  }

  /**
   * Start game
   * @param params Parameters for initialization
   */
  public static start(params: InitializationParams): void {
    if (this.stage !== null) {
      throw new Error('Game has been started.');
    }

    const app = new Application({
      width: params.width,
      height: params.height,
      antialias: params.antialias,
      resolution: params.resolution,
      transparent: params.transparent,
      sharedTicker: true,
      sharedLoader: true
    });
    document.body.appendChild(app.view);

    this.width = params.width;
    this.height = params.height;
    if (params.debug) this.debug = params.debug;

    // pixi shared modules
    this.renderer = app.renderer;
    this.loader = Loader.shared;
    this.ticker = Ticker.shared;

    // layer manager setting
    const layerRoot = new Container();
    layerRoot.name = 'LayerRoot';
    app.stage.addChild(layerRoot);
    this.layerManager = new LayerManager(layerRoot);

    // stage setting
    app.stage.name = 'Root';
    app.stage.x = params.width / 2;
    app.stage.y = params.height / 2;
    this.stage = app.stage;

    // canvas manager
    CanvasManager.setup(app.view, params.width, params.height);

    // ecupdate
    Ticker.shared.add(() => {
      try {
        layerRoot.ecUpdate(Ticker.shared.deltaMS);
        layerRoot.ecUpdateChildren(Ticker.shared.deltaMS);
        Tween.updateAll(Ticker.shared.deltaMS);
      } catch (e) {
        /* istanbul ignore else */
        if (this.updateErrorHandler) {
          this.updateErrorHandler(e);
        } else {
          throw e;
        }
      }
    });

    if (this.debug) {
      const stats = new Stats();
      document.body.appendChild(stats.dom);
      stats.begin();
      const ticker = new Ticker();
      ticker
        .add(() => {
          stats.end();
          stats.begin();
        })
        .start();
    }
  }

  /**
   * Register update-error handler
   * @param handler Error handler
   */
  public static registerUpdateErrorHandler(handler: (e: Error) => void): void {
    this.updateErrorHandler = handler;
  }

  /**
   * Add overlay filter to root container
   * @param filter Overlay filter
   */
  public static addOverlayFilter(filter: Filter): void {
    this.overlayFilters.push(filter);
  }

  /**
   * Set overlay active
   * @param filterOn Whether overlay filter is active
   */
  public static setOverlayFilter(active: boolean): void {
    if (this.stage === null) {
      throw new Error('Game has not been started.');
    }
    if (active) {
      this.stage.filters = this.overlayFilters;
    } else {
      this.stage.filters = [];
    }
  }
}
